import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { totalExpenses } from '../data/expenses';
import { locationData, companyBreakeven } from '../data/locations';
import { DollarSign, Users, TrendingUp, Download, FileText } from 'lucide-react';
import { exportCompanyData } from '../utils/dataExport';
import { generateFinancialReport } from '../utils/reportGenerator';

interface DashboardProps {
  companyId: string | null;
}

const Dashboard: React.FC<DashboardProps> = ({ companyId }) => {
  const locationExpenses = Object.entries(totalExpenses.locations)
    .filter(([name]) => name !== 'Overhead' && name !== 'Admin' && name !== 'Billing' && name !== 'Marketing')
    .map(([name, value]) => ({
      name,
      value
    }));

  const activeLocations = locationData.filter(loc => loc.clients > 0);
  const breakevenData = activeLocations.map(loc => ({
    name: loc.name,
    current: loc.breakeven.current,
    better: loc.breakeven.better
  }));

  const totalClients = activeLocations.reduce((sum, loc) => sum + loc.clients, 0);
  const totalRevenue = activeLocations.reduce((sum, loc) => sum + (loc.clients * loc.breakeven.current), 0);
  const potentialRevenue = activeLocations.reduce((sum, loc) => sum + (loc.clients * loc.breakeven.better), 0);
  const revenueGap = potentialRevenue - totalRevenue;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Financial Overview</h1>
          <p className="text-gray-600 mt-1">Track and analyze your financial metrics</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => companyId && exportCompanyData(companyId)}
            disabled={!companyId}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Download className="h-5 w-5 mr-2" />
            Export Data
          </button>
          <button
            onClick={() => companyId && generateFinancialReport(companyId)}
            disabled={!companyId}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <FileText className="h-5 w-5 mr-2" />
            Generate Report
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Total Expenses</h3>
              <p className="text-2xl font-bold">${totalExpenses.total.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">Monthly operating expenses across all locations</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="bg-green-100 p-3 rounded-full mr-4">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Total Clients</h3>
              <p className="text-2xl font-bold">{totalClients}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">Active clients across all locations</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="bg-purple-100 p-3 rounded-full mr-4">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Revenue Gap</h3>
              <p className="text-2xl font-bold">${revenueGap.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">Potential additional revenue with improved rates</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 overflow-hidden">
          <h2 className="text-xl font-bold mb-4">Location Expenses & Breakeven Analysis</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Monthly Expenses</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Clients</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Current Rate</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Better Rate</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Revenue Gap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activeLocations.map(location => {
                  const expenses = totalExpenses.locations[location.name] || 0;
                  const revenueGap = (location.breakeven.better - location.breakeven.current) * location.clients;
                  return (
                    <tr key={location.name} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{location.name}</td>
                      <td className="px-4 py-2">${expenses.toLocaleString()}</td>
                      <td className="px-4 py-2">{location.clients}</td>
                      <td className="px-4 py-2">${location.breakeven.current.toFixed(2)}</td>
                      <td className="px-4 py-2">${location.breakeven.better.toFixed(2)}</td>
                      <td className="px-4 py-2 text-green-600">+${revenueGap.toFixed(2)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2">${totalExpenses.total.toLocaleString()}</td>
                  <td className="px-4 py-2">{totalClients}</td>
                  <td className="px-4 py-2">${(totalRevenue / totalClients).toFixed(2)}</td>
                  <td className="px-4 py-2">${(potentialRevenue / totalClients).toFixed(2)}</td>
                  <td className="px-4 py-2 text-green-600">+${revenueGap.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Breakeven Analysis</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={breakevenData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value}`} />
                <Legend />
                <Bar dataKey="current" name="Current Rate" fill="#8884d8" />
                <Bar dataKey="better" name="Target Rate" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Company Breakeven Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Current Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Per Visit Rate:</span>
                <span>${companyBreakeven.perVisit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Current Breakeven:</span>
                <span>${companyBreakeven.current.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">Potential Improvements</h3>
            <div className="space-y-4">
              {Object.entries(companyBreakeven.increments).map(([key, value]) => (
                <div key={key} className="flex justify-between border-b pb-2">
                  <span className="font-medium">+{key} Rate Increase:</span>
                  <span>${value.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;